import { i18n } from "@lingui/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SemanticAd } from "src/types/SemanticAd";
import {
	trackSemanticSearchResultClick,
	trackSemanticSearchResultsImpression,
	trackSemanticSearchResultsView,
} from "../../tracking/pulse";
import { markResultsTracked, setSearchQuery } from "../stores/searchStore";
import { Suggestions } from "../suggestions/Suggestions";
import { useSuggestions } from "../suggestions/useSuggestions";
import { ErrorState } from "./ErrorState";
import { FeedbackPrompt } from "./FeedbackPrompt";
import { ResultItem } from "./ResultItem";
import { ResultsLayout } from "./ResultsLayout";
import { SortingInfo } from "./SortingInfo";
import { useSearchResults } from "./useSearchResults";
import { ZeroResults } from "./ZeroResults";

const RESULTS_HEADING_ID = "results-heading";

export type SearchResultProps = {
	isMobile: boolean;
	loginId?: number;
	locale: string;
	currency: string;
	helpCenterUrl: string;
	brand: string;
};

export function SearchResult(props: SearchResultProps) {
	const { isMobile, locale, currency, helpCenterUrl, brand } = props;
	const { results, currentQuery, resultsQuery, hasTrackedResults, showZeroResults, errorState, tracking } =
		useSearchResults();
	const { suggestions, isLoading: suggestionsLoading, error: suggestionsError } = useSuggestions(currentQuery, 10);
	const [feedbackGiven, setFeedbackGiven] = useState(false);
	const lastTrackedKeyRef = useRef<string | null>(null);
	const trackedViewRef = useRef<string | null>(null);

	const resultsCount = results.length;
	const impressionPayload = useMemo(
		() =>
			results.map((ad) => ({
				id: ad.id,
				heading: ad.heading,
			})),
		[results],
	);

	useEffect(() => {
		if (!currentQuery || errorState?.hasError) {
			return;
		}

		// Wait until the results correspond to the active query to avoid double-tracking
		if (resultsQuery !== currentQuery) {
			return;
		}

		if (hasTrackedResults) {
			return;
		}

		const impressionKey = `${currentQuery}:${impressionPayload.map((item) => item.id).join("|")}:${resultsCount}`;
		if (lastTrackedKeyRef.current !== impressionKey) {
			lastTrackedKeyRef.current = impressionKey;
			trackSemanticSearchResultsImpression(currentQuery, impressionPayload);
		}

		const viewKey = `${currentQuery}:${resultsCount}`;
		if (trackedViewRef.current === viewKey) {
			return;
		}
		trackedViewRef.current = viewKey;
		const selectionFilters = tracking?.object?.selectionFilters;
		trackSemanticSearchResultsView(currentQuery, resultsCount, undefined, selectionFilters);
		markResultsTracked();
	}, [currentQuery, resultsQuery, resultsCount]);

	const handleResultClick = useCallback(
		(ad: SemanticAd, position: number) => {
			if (!currentQuery) return;
			trackSemanticSearchResultClick(currentQuery, ad.id, position);
		},
		[currentQuery],
	);

	// Show error state if we have an error
	if (errorState?.hasError && currentQuery) {
		return (
			<ErrorState
				error={errorState}
				helpCenterUrl={helpCenterUrl}
				onRetry={() => {
					// Retry the search with the current query
					setSearchQuery(currentQuery);
				}}
			/>
		);
	}

	const handleSuggestionClick = (suggestion: string) => {
		// Set the suggestion in the search store so it appears in SearchBar
		setSearchQuery(suggestion);
		// Scroll to top of the page when a new suggestion is clicked
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	// Show ZeroResults if we should and have a query but no results (and no error)
	if (showZeroResults && results.length === 0) {
		return <ZeroResults className={`mt-24`} brand={brand} />;
	}

	// Show suggestions when there's no query (initial state)
	if (!currentQuery) {
		return (
			<div>
				<Suggestions
					onClick={handleSuggestionClick}
					suggestions={suggestions}
					isLoading={suggestionsLoading}
					error={suggestionsError}
				/>
			</div>
		);
	}

	// Split results to insert suggestions after first part (4 items) and feedback after second part (20 items)
	const firstPartResults = results.slice(0, 4);
	const secondPartResults = results.slice(4, 20);
	const remainingResults = results.slice(20);
	const shouldShowFirstSuggestionsInline = results.length > 0;
	const shouldShowSecondSuggestionsInline = results.length > 4;
	const shouldShowFeedback = results.length > 0;
	const resultCount = results.length >= 100 ? "99+" : results.length;

	return (
		<section aria-labelledby={RESULTS_HEADING_ID}>
			{/* Show header with title and result count */}
			<div className="flex flex-col mt-24">
				<span id="search-results-title" className="t4 mb-8">
					{i18n.t({
						id: "explore.results.title",
						message: "Based on your search",
					})}
				</span>
				<div className="flex items-center justify-between">
					<span className="text-detail" id="number-of-results">
						{resultCount}{" "}
						{i18n.t({
							id: "explore.results.count.hits",
							message: "hits",
						})}
					</span>
					<SortingInfo />
				</div>
			</div>

			<ResultsLayout className="mt-16" isMobile={isMobile}>
				{firstPartResults.map((ad, idx) => (
					<ResultItem
						ad={ad}
						key={ad.id}
						position={idx}
						isMobile={isMobile}
						onClick={handleResultClick}
						currency={currency}
					/>
				))}
				{shouldShowFirstSuggestionsInline && (
					<div className="col-span-2 md:col-span-4">
						<Suggestions
							inline
							onClick={handleSuggestionClick}
							suggestions={suggestions}
							isLoading={suggestionsLoading}
							error={suggestionsError}
						/>
					</div>
				)}
				{secondPartResults.map((ad, idx) => (
					<ResultItem
						ad={ad}
						key={ad.id}
						position={idx + 4}
						isMobile={isMobile}
						onClick={handleResultClick}
						currency={currency}
					/>
				))}{" "}
				{shouldShowFeedback && !feedbackGiven && (
					<div className="col-span-2 md:col-span-4">
						<FeedbackPrompt query={currentQuery} locale={locale} onFeedbackGiven={() => setFeedbackGiven(true)} />
					</div>
				)}
				{remainingResults.map((ad, idx) => (
					<ResultItem
						ad={ad}
						key={ad.id}
						position={idx + 20}
						isMobile={isMobile}
						onClick={handleResultClick}
						currency={currency}
					/>
				))}
				{shouldShowSecondSuggestionsInline && (
					<div className="col-span-2 md:col-span-4">
						<Suggestions
							inline
							onClick={handleSuggestionClick}
							suggestions={suggestions}
							isLoading={suggestionsLoading}
							error={suggestionsError}
						/>
					</div>
				)}
			</ResultsLayout>
		</section>
	);
}
